import { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  useCreatePaymentPlanMutation,
  useUpdatePaymentPlanMutation,
} from "../store/api/admin/paymentSlice";

const initialState = {
  id: "",
  name: "",
  parent_max_limit: "",
  vehicle_max_limit: "",
  price_cents: "",
  trial_days: "",
  user_type: "",
  description: "",
};

function AddPayment({ handleClose, editPlan, onSuccess }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});

  // Create Payment Plan
  const [createPaymentPlan, { isLoading: isCreating }] =
    useCreatePaymentPlanMutation();

  // Update Payment Plan
  const [updatePaymentPlan, { isLoading: isUpdating }] =
    useUpdatePaymentPlanMutation();

  useEffect(() => {
    if (editPlan) {
      setFormData({
        id: editPlan.id || "",
        name: editPlan.name || "",
        parent_max_limit: editPlan.parent_max_limit?.toString() || "",
        vehicle_max_limit: editPlan.vehicle_max_limit?.toString() || "",
        price_cents: editPlan.price_cents?.toString() || "",
        trial_days: editPlan.trial_days?.toString() || "",
        user_type: editPlan.user_type || "",
        description: editPlan.description || "",
      });
    } else {
      setFormData(initialState);
    }
  }, [editPlan]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: value.trim() ? "" : prevErrors[name],
    }));

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const isFree =
    formData.price_cents !== "" && Number(formData.price_cents) === 0;

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    Object.entries(formData).forEach(([key, value]) => {
      if (key === "id") return;
      if (key === "trial_days") return; // validated separately below
      if (
        value === null ||
        value === undefined ||
        (typeof value === "string" && value.trim() === "")
      ) {
        newErrors[key] = `${t("errors." + key)} ${t("errors.required")}`;
        isValid = false;
      }
    });

    if (formData.user_type === "") {
      newErrors.user_type = `${t("user")} ${t("type")} ${t("is")} ${t(
        "required",
      )}`;
      isValid = false;
    }

    if (isFree) {
      const days = Number(formData.trial_days);
      if (formData.trial_days === "" || isNaN(days)) {
        newErrors.trial_days = "Trial days is required";
        isValid = false;
      } else if (days < 1 || days > 31) {
        newErrors.trial_days = "Trial days must be between 1 and 31";
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        let response;
        if (formData.id) {
          response = await updatePaymentPlan({
            id: formData.id,
            data: formData,
          }).unwrap();
        } else {
          // Create new plan
          response = await createPaymentPlan(formData).unwrap();
        }

        if (response.statusCode === 200 || response.statusCode === 201) {
          toast.success(response.message);
          onSuccess();
        }
      } catch (err) {
        const mesg = err.data?.message || "Something went wrong";
        toast.error(mesg);
      }
    }
  };

  return (
    <div className="popupBox p-3">
      <ul className="form p-0 m-0">
        <li>
          <label className="filedHd">{t("name")}</label>
          <input
            className="formInput"
            placeholder={`${t("name")}`}
            name="name"
            value={formData.name}
            onChange={handleChange}
          />
          {errors.name && <span style={{ color: "red" }}>{errors.name}</span>}
        </li>

        <li>
          <label className="filedHd">
            {t("max")} {t("seats")} ({t("riders")})
          </label>
          <input
            type="number"
            className="formInput"
            placeholder={`${t("max")} ${t("seats")} (${t("riders")})`}
            name="parent_max_limit"
            value={formData.parent_max_limit}
            onChange={handleChange}
          />
          {errors.parent_max_limit && (
            <span style={{ color: "red" }}>{errors.parent_max_limit}</span>
          )}
        </li>

        <li>
          <label className="filedHd">
            {t("max")} {t("vehicles")} {t("limit")}
          </label>
          <input
            type="number"
            className="formInput"
            placeholder={`${t("max")} ${t("vehicles")} ${t("limit")}`}
            name="vehicle_max_limit"
            value={formData.vehicle_max_limit}
            onChange={handleChange}
          />
          {errors.vehicle_max_limit && (
            <span style={{ color: "red" }}>{errors.vehicle_max_limit}</span>
          )}
        </li>

        <li>
          <label className="filedHd">
            {t("price")} {t("cents")}
          </label>
          <input
            type="number"
            className="formInput"
            placeholder={`${t("price")} ${t("cents")}`}
            name="price_cents"
            value={formData.price_cents}
            onChange={handleChange}
          />
          {errors.price_cents && (
            <span style={{ color: "red" }}>{errors.price_cents}</span>
          )}
        </li>

        {isFree && (
          <li>
            <label className="filedHd">Trial Days</label>
            <input
              type="number"
              className="formInput"
              placeholder="Trial Days (0 - 31)"
              name="trial_days"
              value={formData.trial_days}
              onChange={handleChange}
              min={1}
              max={31}
            />
            {errors.trial_days && (
              <span style={{ color: "red" }}>{errors.trial_days}</span>
            )}
          </li>
        )}

        <li>
          <label className="filedHd">
            {t("user")} {t("type")}
          </label>
          <select
            name="user_type"
            value={formData.user_type}
            onChange={handleChange}
            className="formSelect"
          >
            <option value="">
              {t("select")} {t("user")} {t("type")}
            </option>
            <option value="school">{t("school")}</option>
            <option value="vendor">{t("vendor")}</option>
          </select>
          {errors.user_type && (
            <span style={{ color: "red" }}>{errors.user_type}</span>
          )}
        </li>

        <li>
          <label className="filedHd">{t("description")}</label>
          <textarea
            className="formInput"
            placeholder="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
          />
          {errors.description && (
            <span style={{ color: "red" }}>{errors.description}</span>
          )}
        </li>

        <li className="d-flex">
          <div className="col-6 pe-2 pt-2">
            <Button className="smallBtn full cancel" onClick={handleClose}>
              {t("btns.cancel")}
            </Button>
          </div>
          <div className="col-6 ps-2 pt-2">
            <Button
              className="smallBtn full"
              onClick={handleSubmit}
              disabled={isCreating || isUpdating}
            >
              {isCreating || isUpdating ? (
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                >
                  <span className="visually-hidden">{t("loading")}...</span>
                </span>
              ) : (
                t("btns.submit")
              )}
            </Button>
          </div>
        </li>
      </ul>
    </div>
  );
}

export default AddPayment;
